/**
 * DashboardHeader Component
 * Top navigation bar with logo, study selector, navigation buttons, and user menu
 */
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  FileText,
  RefreshCw,
  Settings,
  LogOut,
  User,
  Filter,
  Loader2,
  FileArchive,
  Bell,
  BarChart3,
  Sun,
  Moon,
  Activity,
  Key,
  Shield,
  FolderTree,
  Search
} from 'lucide-react';
import MobileNavigation from '../../../components/MobileNavigation';

// eTMF Vault Logo URL
const ETMF_VAULT_LOGO = "https://www.vetforcesolutions.com/backend/uploads/8e6f835bdae1ff1ee6c1d6b663d984dc.png";

export default function DashboardHeader({
  user,
  studies,
  selectedStudy,
  setSelectedStudy,
  contextStudy,
  clearStudySelection,
  isAdmin,
  canExport,
  exporting,
  isDark,
  toggleTheme,
  handleLogout,
  handleExportStudy,
  setShowSearchModal,
  setMfaSettingsOpen,
  setShowAllDocs,
  setSelectedFolder,
  setSearchQuery,
  setSmartSearchResults
}) {
  const navigate = useNavigate();

  const handleLogoClick = () => {
    setShowAllDocs(true);
    setSelectedFolder(null);
    setSearchQuery('');
    setSmartSearchResults(null);
  };

  return (
    <header className="app-header h-14 sm:h-16 px-2 sm:px-4 flex items-center">
      {/* Mobile Menu Button */}
      <div className="lg:hidden mr-2">
        <MobileNavigation 
          user={user} 
          onLogout={handleLogout}
          studyName={contextStudy?.study_name}
        />
      </div>

      {/* Left Section - Logo & Study */}
      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <div 
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleLogoClick}
          title="Go to All Documents"
        >
          <img 
            src={ETMF_VAULT_LOGO} 
            alt="eTMF Vault" 
            className="w-24 sm:w-28 md:w-36 lg:w-40 h-auto object-contain"
            style={{ filter: 'brightness(1.1)' }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="w-8 h-8 rounded-lg bg-blue-600 items-center justify-center hidden">
            <FileText className="w-4 h-4 text-white" />
          </div>
        </div>

        <div className="h-5 w-px bg-white/20 hidden sm:block" />

        {/* Study Context Display - Hidden on mobile */}
        {contextStudy ? (
          <div className="hidden sm:flex items-center gap-2">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-white truncate max-w-[100px] md:max-w-[120px]">{contextStudy.study_name}</span>
              <span className="text-[10px] text-white/60 truncate max-w-[100px] md:max-w-[120px]">{contextStudy.protocol_number} • {contextStudy.role_display}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearStudySelection}
              className="h-7 w-7 p-0 text-white/60 hover:text-white hover:bg-white/10"
              title="Switch Study"
              data-testid="switch-study-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <Select value={selectedStudy || ''} onValueChange={setSelectedStudy}>
            <SelectTrigger className="w-52 h-9 bg-white/10 border-white/20 text-white hover:bg-white/15" data-testid="study-selector">
              <SelectValue placeholder="Select a study" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {studies.map(study => (
                <SelectItem key={study.id} value={study.id} className="text-white hover:bg-slate-700">
                  {study.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Center Section - Main Actions - Hidden on mobile, shown on lg+ */}
      <div className="flex-1 hidden lg:flex items-center justify-center gap-1 overflow-x-auto px-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/analytics')}
          className="h-8 px-2 rounded-full bg-gradient-to-r from-red-500/20 to-rose-500/20 border-red-400/30 text-white hover:from-blue-500/30 hover:to-purple-500/30 hover:text-white text-xs flex-shrink-0"
        >
          <BarChart3 className="w-3 h-3 mr-1" />
          Analytics
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/compliance')}
          className="h-8 px-2 rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-400/30 text-white hover:from-green-500/30 hover:to-emerald-500/30 hover:text-white text-xs flex-shrink-0"
        >
          <Shield className="w-3 h-3 mr-1" />
          Compliance
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/tmf-health')}
          className="h-8 px-2 rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-400/30 text-white hover:from-blue-500/30 hover:to-cyan-500/30 hover:text-white text-xs flex-shrink-0"
          data-testid="tmf-health-btn"
        >
          <Activity className="w-3 h-3 mr-1" />
          TMF
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/etmf-management')}
          className="h-8 px-2 rounded-full bg-gradient-to-r from-teal-500/20 to-emerald-500/20 border-teal-400/30 text-white hover:from-teal-500/30 hover:to-emerald-500/30 hover:text-white text-xs flex-shrink-0"
          data-testid="etmf-management-btn"
        >
          <FolderTree className="w-3 h-3 mr-1" />
          eTMF
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/reports')}
          className="h-8 px-2 rounded-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-indigo-400/30 text-white hover:from-indigo-500/30 hover:to-purple-500/30 hover:text-white text-xs flex-shrink-0"
          data-testid="reports-btn"
        >
          <BarChart3 className="w-3 h-3 mr-1" />
          Reports
        </Button>

        {isAdmin() && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/api-integration')}
            className="h-8 px-2 rounded-full bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border-purple-400/30 text-white hover:from-purple-500/30 hover:to-indigo-500/30 hover:text-white text-xs flex-shrink-0"
            data-testid="api-integration-btn"
          >
            <Key className="w-3 h-3 mr-1" />
            API
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSearchModal(true)}
          className="h-8 px-2 rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white text-xs flex-shrink-0"
        >
          <Filter className="w-3 h-3 mr-1" />
          Search
        </Button>

        {selectedStudy && canExport() && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportStudy}
            disabled={exporting}
            className="h-8 px-2 rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white text-xs flex-shrink-0"
          >
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileArchive className="w-3 h-3 mr-1" />}
            Export
          </Button>
        )}
      </div>

      {/* Mobile Quick Actions */}
      <div className="flex-1 flex lg:hidden items-center justify-end gap-1 px-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSearchModal(true)}
          className="h-9 w-9 p-0 text-white hover:bg-white/10"
        >
          <Search className="w-4 h-4" />
        </Button>
      </div>

      {/* Right Section - Theme Toggle, Admin & User */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleTheme}
          className="h-8 w-8 rounded-full text-white hover:bg-white/10 p-0"
          title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>

        {isAdmin() && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            data-testid="admin-panel-btn"
            className="h-8 px-2 rounded-full text-white hover:bg-white/10 hover:text-white text-xs"
          >
            <Settings className="w-3 h-3 mr-1" />
            Admin
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="rounded-full gap-1 text-white hover:bg-white/10 h-8 px-2" data-testid="user-menu-btn">
              <div className="w-6 h-6 rounded-full bg-red-600/20 flex items-center justify-center">
                <User className="w-3 h-3 text-red-400" />
              </div>
              <span className="text-xs font-medium text-white max-w-[80px] truncate">{user?.name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-slate-800 border-slate-700">
            <div className="px-3 py-2 border-b border-slate-700">
              <p className="text-sm font-medium text-white">{user?.name}</p>
              <p className="text-xs text-slate-400">{user?.email}</p>
              <span className={`role-badge ${user?.role} mt-2`}>{user?.role}</span>
            </div>
            <DropdownMenuItem onClick={() => navigate('/notification-settings')} className="text-white hover:bg-slate-700">
              <Bell className="w-4 h-4 mr-2" />
              Notification Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setMfaSettingsOpen(true)} className="text-white hover:bg-slate-700">
              <Shield className="w-4 h-4 mr-2" />
              Security Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="text-white hover:bg-slate-700">
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
